import { Controller, useFormContext, useWatch } from 'react-hook-form'
import {
  CurecallInput,
  CurecallInputLabel,
  CurecallSearchableSelect,
  CurecallTextarea,
  CurecallToggle,
  TOption,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useId, useMemo, useState } from 'react'
import { EDurationUnit, TChatbotGeneralFormValues } from '../chatbot-form.type'

type TDurationUnitOption = TOption<EDurationUnit>

export function ChatbotAutoEndField() {
  const { t } = useTranslation()
  const id = useId()

  const ACTIVITY_DURATION_UNIT_OPTIONS: TDurationUnitOption[] = useMemo(
    () => [
      {
        label: t('pages.configuration.followups.chatbot.general-form.duration-unit-minutes'),
        value: EDurationUnit.MINUTES,
      },
      {
        label: t('pages.configuration.followups.chatbot.general-form.duration-unit-hours'),
        value: EDurationUnit.HOURS,
      },
      {
        label: t('pages.configuration.followups.chatbot.general-form.duration-unit-days'),
        value: EDurationUnit.DAYS,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    formState: { errors, defaultValues },
    control,
    setValue,
    clearErrors,
    unregister,
  } = useFormContext<TChatbotGeneralFormValues>()

  const automaticEnd = useWatch({
    control,
    name: 'options.automaticEnd',
  })

  const [showExpirationMessage, setShowExpirationMessage] = useState(
    !!defaultValues?.options?.automaticEnd?.expirationMessage,
  )
  const [selectedDurationUnit, setSelectedDurationUnit] = useState<TDurationUnitOption>(
    defaultValues?.options?.automaticEnd?.expireDurationUnit
      ? (ACTIVITY_DURATION_UNIT_OPTIONS.find(
          ({ value }) => value === defaultValues?.options?.automaticEnd?.expireDurationUnit,
        ) as TDurationUnitOption)
      : (ACTIVITY_DURATION_UNIT_OPTIONS[1] as TDurationUnitOption),
  )

  const handleChangeDurationUnit = (option: TDurationUnitOption) => {
    setSelectedDurationUnit(option)
    setValue('options.automaticEnd.expireDurationUnit', option.value)
    clearErrors('options.automaticEnd.expireDurationUnit')
  }

  return (
    <div className="c-flex c-flex-col c-gap-4">
      <Controller
        control={control}
        name="options.automaticEnd"
        render={({ field }) => (
          <CurecallToggle
            leftLabel={t('pages.configuration.followups.chatbot.general-form.automatic-end')}
            isEnabled={!!field.value}
            onChange={(enabled) => {
              if (enabled) {
                setValue('options.automaticEnd', {
                  expireDurationUnit: (ACTIVITY_DURATION_UNIT_OPTIONS[1] as TDurationUnitOption).value,
                })
              } else {
                setValue('options.automaticEnd', null)
                clearErrors('options.automaticEnd')
                unregister([
                  'options.automaticEnd.expireDuration',
                  'options.automaticEnd.expireDurationUnit',
                  'options.automaticEnd.expirationMessage',
                ])
                setShowExpirationMessage(false)
              }
            }}
            tooltipContent={t('pages.configuration.followups.chatbot.general-form.automatic-end-tooltip')}
            tooltipPosition="right"
          />
        )}
      />

      {automaticEnd && (
        <div className="c-flex c-flex-col c-pl-6 c-border-l-2 c-border-gray-200 c-mb-4">
          <CurecallInputLabel
            id={id}
            label={t('pages.configuration.followups.chatbot.general-form.activity-duration')}
            isRequired={true}
            tooltipContent={t('pages.configuration.followups.chatbot.general-form.activity-duration-tooltip')}
            tooltipPosition="right"
          />
          <div className="c-flex c-gap-3 c-items-end">
            <Controller
              control={control}
              name="options.automaticEnd.expireDuration"
              rules={{
                required: !!automaticEnd,
                min: automaticEnd ? 1 : undefined,
              }}
              render={({ field }) => (
                <CurecallInput
                  aria-labelledby={id}
                  placeholder="0"
                  value={field.value?.toString() || ''}
                  onChange={(e) => field.onChange(Number.parseInt(e.target.value) || 0)}
                  type="number"
                  errorMessage={
                    errors.options?.automaticEnd?.expireDuration &&
                    t('pages.configuration.followups.chatbot.general-form.activity-duration-error')
                  }
                  errorClassName="c-text-nowrap"
                  wrapperClassName="c-w-24"
                />
              )}
            />

            <Controller
              control={control}
              name="options.automaticEnd.expireDurationUnit"
              rules={{ required: !!automaticEnd }}
              render={() => (
                <CurecallSearchableSelect
                  items={ACTIVITY_DURATION_UNIT_OPTIONS}
                  onSelect={handleChangeDurationUnit}
                  defaultItemSelected={selectedDurationUnit}
                  placeholder={t('pages.configuration.followups.chatbot.general-form.select-duration-unit')}
                  wrapperClassName="c-w-[200px]"
                />
              )}
            />

            <CurecallToggle
              leftLabel={t('pages.configuration.followups.chatbot.general-form.show-expiration-message')}
              isEnabled={showExpirationMessage}
              onChange={(enabled) => {
                setShowExpirationMessage(enabled)
                if (!enabled) {
                  setValue('options.automaticEnd.expirationMessage', undefined)
                }
              }}
              tooltipContent={t(
                'pages.configuration.followups.chatbot.general-form.show-expiration-message-tooltip',
              )}
              tooltipPosition="right"
              tooltipWrapperClassName="tw-w-max"
            />
          </div>

          {showExpirationMessage && (
            <Controller
              control={control}
              name="options.automaticEnd.expirationMessage"
              rules={{
                required: showExpirationMessage,
                maxLength: showExpirationMessage ? 500 : undefined,
              }}
              render={({ field }) => (
                <CurecallTextarea
                  label={t('pages.configuration.followups.chatbot.general-form.expiration-message')}
                  placeholder={t(
                    'pages.configuration.followups.chatbot.general-form.expiration-message-placeholder',
                  )}
                  value={field.value || ''}
                  onChange={field.onChange}
                  isRequired={showExpirationMessage}
                  errorMessage={
                    errors.options?.automaticEnd?.expirationMessage &&
                    t('pages.configuration.followups.chatbot.general-form.expiration-message-error')
                  }
                  tooltipContent={t(
                    'pages.configuration.followups.chatbot.general-form.expiration-message-tooltip',
                  )}
                  tooltipPosition="right"
                  textareaClassName="!c-font-light"
                  wrapperClassName="c-mt-6"
                  maxLength={500}
                />
              )}
            />
          )}
        </div>
      )}
    </div>
  )
}
